import { useRef, useState } from 'react';
import { Icon } from '@iconify-icon/react';
import { Modal } from '@mantine/core';

const videoSource = '/videos/journey/chatco-journey.mp4?v=20260923';
const videoPoster = '/images/journey/journey-poster.jpg?v=20260923';

function JourneyPlayer({ startOnMount = false }: { startOnMount?: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(startOnMount);

  const play = () => {
    setStarted(true);
    void videoRef.current?.play().catch(() => videoRef.current?.focus());
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#eeeade]">
      <video
        ref={videoRef}
        width={1920}
        height={1080}
        className="aspect-video w-full"
        aria-label="How Chat.co works: from your knowledge to instant answers"
        poster={videoPoster}
        preload="none"
        controls={started}
        autoPlay={startOnMount}
        playsInline
        onPlay={() => setStarted(true)}>
        <source src={videoSource} type="video/mp4" />
        <track
          kind="captions"
          src="/videos/journey/chatco-journey.en.vtt?v=20260923"
          srcLang="en"
          label="English"
        />
        Your browser does not support embedded video. <a href={videoSource}>Watch the film</a>.
      </video>
      {!started ? (
        <button
          type="button"
          onClick={play}
          aria-label="Play the 37-second Chat.co explainer"
          className="group absolute inset-0 flex items-end justify-center bg-gradient-to-t from-[#031b33]/45 via-transparent to-transparent pb-5 focus-visible:outline focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-blue-500 sm:pb-7">
          <span className="inline-flex items-center gap-3 rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#004790] shadow-lg transition-transform group-hover:scale-105">
            <Icon icon="tabler:player-play-filled" aria-hidden="true" className="text-xl" />
            Watch the story{' '}
            <span className="border-l border-blue-100 pl-3 text-gray-500">0:37</span>
          </span>
        </button>
      ) : null}
    </div>
  );
}

export function JourneyFilmModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="How Chat.co works · 0:37"
      centered
      size="min(960px, calc(100vw - 32px))"
      radius="lg"
      closeButtonProps={{ 'aria-label': 'Close explainer video' }}>
      {opened ? <JourneyPlayer startOnMount /> : null}
    </Modal>
  );
}

export default function JourneyFilm({ modalOpened = false }: { modalOpened?: boolean }) {
  return (
    <section
      id="journey-film"
      aria-labelledby="journey-heading"
      className="scroll-mt-24 bg-[#f8f6f0] px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="mx-auto grid max-w-7xl items-center gap-8 sm:px-4 lg:grid-cols-[0.7fr_1fr] lg:gap-12">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#004790]">
            The Chat.co story
          </p>
          <h2
            id="journey-heading"
            className="mt-4 text-3xl font-bold tracking-tight text-gray-950 sm:text-4xl">
            From your knowledge to instant answers.
          </h2>
          <p className="mt-5 max-w-lg text-base leading-7 text-gray-600">
            Meet a business with questions coming from every direction. See how its knowledge
            becomes a helpful AI agent for customers and the team.
          </p>
          <div className="mt-7 flex flex-wrap gap-x-5 gap-y-3 text-sm font-medium text-[#004790]">
            <span>01 · Connect</span>
            <span>02 · Make it yours</span>
            <span>03 · Go live</span>
          </div>
          <p className="mt-6 text-sm text-gray-500">The whole journey, in 37 seconds.</p>
        </div>
        {/* Reset inline playback when the modal toggles to prevent overlapping audio. */}
        <JourneyPlayer key={modalOpened ? 'modal-open' : 'inline'} />
      </div>
    </section>
  );
}
